import React, { useMemo } from "react"
import 'swiper/css';
import 'swiper/css/pagination';
import { useDispatch, useSelector } from "react-redux";
import IGPostIcon from "assets/icons/mono/IGPostIcon";
import IGStoryIcon from "assets/icons/mono/IGStoryIcon";
import ReelsIcon from "assets/icons/ReelsIcon";
import CollapsableContent from "Components/Common/CollapsableContent";
import InstagramCaption from "Components/Common/InstagramCaption";
import { Badge } from "reactstrap";
import { AppDispatch, RootState } from "store";
import { formatNumber, toValidNumber } from "utils/helpers";
import { showInfluencerPostModal } from "slices/ui/reducer";
import { useParams } from "react-router-dom";
import { CollaborationPost } from "types/collaboration";

const CollaborationContent: React.FC<{ collaborationPosts: CollaborationPost[] }> = ({ collaborationPosts }) => {

    const { campaignId, collaborationId } = useParams();

    const dispatch: AppDispatch = useDispatch();

    const collaboration = useSelector((state: RootState) => state.legacyCollaborations.items[campaignId!]?.[collaborationId!]?.collaboration);
    const influencer = collaboration?.influencer;
    const socialAccount = collaboration?.social_account;
    const reels = useMemo(() => {
        return collaborationPosts.filter((cp) => cp.social_post.type === "REELS");
    }, [collaborationPosts])

    const posts = useMemo(() => {
        return collaborationPosts.filter((cp) => cp.social_post.type === "FEED");
    }, [collaborationPosts])

    const stories = useMemo(() => {
        return collaborationPosts.filter((cp) => cp.social_post.type === "STORY");
    }, [collaborationPosts])

    const videos = useMemo(() => {
        return collaborationPosts.filter((cp) => cp.social_post.type === "VIDEO");
    }, [collaborationPosts])

    const viewPost = (post: CollaborationPost) => {
        dispatch(showInfluencerPostModal({ postId: post.social_post.uuid, influencer, socialAccount, status: post.status as "pending" | "approved" | "rejected" | "processing" }))
    }

    return (
        <div>
            {
                ['Reels', 'Posts', 'Stories', 'Videos'].map((contentType, i) => {
                    if(contentType === 'Reels' && reels.length === 0) return null;
                    if(contentType === 'Posts' && posts.length === 0) return null;
                    if(contentType === 'Stories' && stories.length === 0) return null;
                    if(contentType === 'Videos' && videos.length === 0) return null;
                    return (
                        <div key={i} className="specification-card">
                            <h6>{contentType}</h6>
                            {
                                (contentType === 'Reels' ? reels : (contentType === 'Posts' ? posts : (contentType === 'Stories' ? stories : (contentType === 'Videos' ? videos : [])))).length > 0 &&
                                <React.Fragment>
                                    {
                                        (contentType === 'Reels' ? reels : (contentType === 'Posts' ? posts : (contentType === 'Stories' ? stories : (contentType === 'Videos' ? videos : [])))).map((post, i) => {
                                            return (
                                                <div key={i} className="card-body d-flex mt-2 flex-row">
                                                    <div onClick={() => viewPost(post)} className="position-relative post-thumbnail" style={{ backgroundImage: `url(${post.social_post.thumbnail_url})` }}>
                                                        {
                                                            post.status === 'approved' && <span className="post-status-badge approved">Approved</span>
                                                        }
                                                        {
                                                            post.status === 'pending' && <span className="post-status-badge new">New</span>
                                                        }
                                                        {
                                                            post.status === 'rejected' && <span className="post-status-badge declined"> Declined </span>
                                                        }
                                                        {
                                                            post.social_post?.ig?.media_product_type === 'REELS' &&
                                                            <span className="media-type-icon">
                                                                <ReelsIcon color="#FFFFFF" />
                                                            </span>
                                                        }
                                                        {
                                                            post.social_post?.ig?.media_product_type === 'STORY' &&
                                                            <span className="media-type-icon">
                                                                <IGStoryIcon />
                                                            </span>
                                                        }
                                                        {
                                                            post.social_post?.ig?.media_product_type === 'VIDEO' &&
                                                            <span className="media-type-icon">
                                                                <i className="ri-youtube-fill" style={{ fontSize: '24px', color: '#FFFFFF' }}></i>
                                                            </span>
                                                        }
                                                        {
                                                            !["REELS", "STORY", "VIDEO"].includes(post.social_post?.ig?.media_product_type!) &&
                                                            <span className="media-type-icon">
                                                                <IGPostIcon />
                                                            </span>
                                                        }
                                                    </div>
                                                    <div className="d-flex flex-fill flex-column justify-content-between align-items-between ms-3" style={{ minHeight: '200px' }}>
                                                        <div>

                                                            {
                                                                collaboration?.platform === 'ig' && post.social_post?.ig?.caption &&
                                                                <CollapsableContent>
                                                                    <InstagramCaption text={post.social_post?.ig?.caption} />
                                                                </CollapsableContent>
                                                            }
                                                            {collaboration?.platform === 'yt' && post.social_post?.yt?.title &&
                                                                <div className="mb-2">
                                                                    <strong>{post.social_post?.yt?.title}</strong>
                                                                </div>
                                                            }
                                                        </div>

                                                        <div className="d-flex justify-content-end gap-3">
                                                            <span><i className="ri-heart-fill"></i> {formatNumber(toValidNumber(post.social_post.like_count))}</span>
                                                            <span><i className="ri-message-2-fill"></i>  {formatNumber(toValidNumber(post.social_post.comments_count))}</span>
                                                            <span><i className="ri-eye-fill"></i> {formatNumber(toValidNumber(post.social_post.views_count))}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })
                                    }
                                </React.Fragment>
                            }
                            {
                                (contentType === 'Reels' ? reels : (contentType === 'Posts' ? posts : (contentType === 'Stories' ? stories : (contentType === 'Videos' ? videos : [])))).length === 0 && <span className="w-100 d-flex justify-content-center p-4">No Content Posted</span>
                            }
                        </div>
                    )
                })
            }
            {
                collaboration?.platform === 'ig' && reels.length + posts.length + stories.length === 0 &&
                <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                    <div className="length-zero m-0">
                        <h3>No content posted!</h3>
                    </div>
                </div>
            }
            {
                collaboration?.platform === 'yt' && videos.length === 0 &&
                <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                    <div className="length-zero m-0">
                        <h3>No content posted!</h3>
                    </div>
                </div>
            }
        </div>
    )
}

export default CollaborationContent